<template>
    <section
      ref="sectionRef"
      class="more-banner"
      :class="{
        'more-banner--visible': isVisible
      }"
    >
      <div class="more-banner__inner">
        <!-- Title -->
        <h2 class="more-banner__title">
          И это ещё не всё<span>!</span>
        </h2>
  
        <!-- Illustration -->
        <div class="more-banner__visual">
          <div class="ambient ambient--left"></div>
          <div class="ambient ambient--right"></div>
  
          <img
            :src="currentImage"
            class="more-banner__image"
            alt="Умная сортировка кандидатов HR3"
            draggable="false"
          />

          <div class="sorting-analysis">
            <span class="sorting-analysis__label">
                Умная сортировка
            </span>

            <h2 class="sorting-analysis__title">
                Не нужно просматривать десятки откликов
            </h2>

            <div class="sorting-analysis__content">
                <p>
                Наш AI уже проанализировал каждого соискателя,
                оценил его соответствие вакансии и автоматически
                отсортировал список для вас.
                </p>

                <p>
                Кандидаты с наиболее высоким рабочим мэтчем
                поднимаются наверх — именно их стоит посмотреть
                в первую очередь.
                </p>

                <p>
                При оценке мы учитываем релевантный опыт,
                профессиональные навыки, требования вакансии
                и особенности вашей компании.
                </p>

                <p class="sorting-analysis__result">
                Вы сразу видите самых перспективных кандидатов
                и можете сосредоточиться на тех, с кем действительно
                стоит продолжить общение.
                </p>
            </div>
            </div>

        </div>
      </div>
    </section>
  
    <div class="separator"></div>
  </template>
  
  <script setup>
  import {
    computed,
    onMounted,
    onUnmounted,
    ref
  } from "vue";
  
  const lightImage = new URL(
    "../../assets/MoreBanner-light.png",
    import.meta.url
  ).href;
  
  const darkImage = new URL(
    "../../assets/MoreBanner-dark.png",
    import.meta.url
  ).href;
  
  const sectionRef = ref(null);
  
  const isVisible = ref(false);
  
  const theme = ref(
    document.documentElement.getAttribute(
      "data-theme"
    ) || "light"
  );
  
  const currentImage = computed(() => {
    return theme.value === "dark"
      ? darkImage
      : lightImage;
  });
  
  let themeObserver = null;
  let intersectionObserver = null;
  
  onMounted(() => {
    themeObserver = new MutationObserver(() => {
      theme.value =
        document.documentElement.getAttribute(
          "data-theme"
        ) || "light";
    });
  
    themeObserver.observe(
      document.documentElement,
      {
        attributes: true,
        attributeFilter: [
          "data-theme"
        ]
      }
    );
  
    intersectionObserver =
      new IntersectionObserver(
        entries => {
          const entry = entries[0];
  
          if (!entry?.isIntersecting) {
            return;
          }
  
          isVisible.value = true;
  
          intersectionObserver?.disconnect();
        },
        {
          threshold: 0.12
        }
      );
  
    if (sectionRef.value) {
      intersectionObserver.observe(
        sectionRef.value
      );
    } else {
      requestAnimationFrame(() => {
        isVisible.value = true;
      });
    }
  });
  
  onUnmounted(() => {
    themeObserver?.disconnect();
    intersectionObserver?.disconnect();
  });
  </script>
  
  <style scoped>
  * {
    box-sizing: border-box;
  }
  
  .more-banner {
    position: relative;
  
    width: 100%;
  
    overflow: hidden;
  
    background: var(--background);
  
    opacity: 0;
    transform: translateY(18px);
  
    transition:
      opacity 0.75s ease,
      transform 0.75s
        cubic-bezier(
          0.22,
          1,
          0.36,
          1
        );
  }
  
  .more-banner--visible {
    opacity: 1;
    transform: translateY(0);
  }
  
  .more-banner__inner {
    width: 100%;
    max-width: 1440px;
  
    margin: 0 auto;
  
    padding:
      150px
      64px
      0px;
  }
  
  /* Title */
  
  .more-banner__title {
    position: relative;
    z-index: 5;
  
    margin:
      0
      0
      18px;
  
    color: var(--text);
  
    font-family:
      "Eastman-Bold",
      sans-serif;
  
    font-size:
      clamp(
        42px,
        4.3vw,
        64px
      );
  
    line-height: 1;
    letter-spacing: -0.045em;
  }
  
  .more-banner__title span {
    color: #2277e6;
  }
  
  /* Visual */
  
  .more-banner__visual {
    position: relative;
  
    width: 100%;
  
    display: flex;
    align-items: center;
    justify-content: center;
  
    isolation: isolate;
  }
  
  .more-banner__image {
    position: relative;
    z-index: 2;
    
    width: 100%;
    max-width: 1320px;
    height: auto;
  
    display: block;
  
    object-fit: contain;
  
    user-select: none;
  
    opacity: 0;
  
    transform:
      translateY(14px)
      scale(0.985);
  
    filter:
      drop-shadow(
        0
        28px
        50px
        rgba(
          32,
          67,
          120,
          0.055
        )
      );
  
    transition:
      opacity 0.9s ease 0.12s,
      transform 0.9s
        cubic-bezier(
          0.22,
          1,
          0.36,
          1
        )
        0.12s;
  }
  
  .more-banner--visible
  .more-banner__image {
    opacity: 1;
  
    transform:
      translateY(0)
      scale(1);
  }
  
  /* Ambient */
  
  .ambient {
    position: absolute;
    z-index: 0;
  
    border-radius: 50%;
  
    pointer-events: none;
  
    filter: blur(110px);
  
    opacity: 0.11;
  
    animation:
      ambientFloat
      16s
      ease-in-out
      infinite;
  }
  
  .ambient--left {
    width: 350px;
    height: 350px;
  
    left: 8%;
    top: 14%;
  
    background: #4b8ff7;
  }
  
  .ambient--right {
    width: 300px;
    height: 300px;
  
    right: 8%;
    bottom: 8%;
  
    background: #7567f1;
  
    animation-delay: -7s;
  }
  
  @keyframes ambientFloat {
    0%,
    100% {
      transform:
        translate3d(
          0,
          0,
          0
        )
        scale(1);
    }
  
    50% {
      transform:
        translate3d(
          18px,
          -15px,
          0
        )
        scale(1.05);
    }
  }
  
  /* Separator */
  
  .separator {
    width: 100%;
    height: 1px;
  
    background:
      var(
        --separator_home,
        rgba(
          0,
          0,
          0,
          0.08
        )
      );
  }
  
  /* Dark */
  
  html[data-theme="dark"]
  .more-banner__image {
    filter:
      drop-shadow(
        0
        28px
        55px
        rgba(
          0,
          0,
          0,
          0.2
        )
      );
  }
  
  html[data-theme="dark"]
  .ambient {
    opacity: 0.16;
  }
  
  /* Laptop */
  
  @media (max-width: 1366px) {
    .more-banner__inner {
      padding:
        56px
        48px
        38px;
    }
  
    .more-banner__title {
      margin-bottom: 12px;
  
      font-size:
        clamp(
          38px,
          4vw,
          54px
        );
    }
  
    .more-banner__image {
      max-width: 1160px;
    }
  }
  
  /* 13" laptop */
  
  @media (
    min-width: 769px
  ) and (
    max-height: 820px
  ) {
    .more-banner__inner {
      padding:
        40px
        46px
        26px;
    }
  
    .more-banner__title {
      margin-bottom: 8px;
  
      font-size:
        clamp(
          36px,
          3.7vw,
          48px
        );
    }
  
    .more-banner__image {
      max-width: 1040px;
    }
  }
  
  /* Tablet */
  
  @media (max-width: 900px) {
    .more-banner__inner {
      padding:
        48px
        28px
        32px;
    }
  
    .more-banner__title {
      font-size:
        clamp(
          36px,
          7vw,
          50px
        );
    }
  
    .more-banner__image {
      width: 112%;
      max-width: none;
    }
  }
  
  /* Mobile */
  
  @media (max-width: 768px) {
    .more-banner__inner {
      padding:
        38px
        18px
        26px;
    }
  
    .more-banner__title {
      margin-bottom: 18px;
  
      font-size: 35px;
      line-height: 1.03;
    }
  
    .more-banner__visual {
      margin:
        0
        -12px;
    }
  
    .more-banner__image {
      width: 124%;
  
      max-width: none;
  
      margin-left: 3%;
  
      filter: none;
    }
  
    .ambient {
      display: none;
    }
  }
  
  @media (max-width: 480px) {
    .more-banner__inner {
      padding:
        32px
        16px
        22px;
    }
  
    .more-banner__title {
      font-size: 30px;
    }
  
    .more-banner__image {
      width: 138%;
  
      margin-left: 6%;
    }
  }
  
  /* Reduced motion */
  
  @media (
    prefers-reduced-motion:
    reduce
  ) {
    .more-banner,
    .more-banner__image,
    .ambient {
      animation: none;
      transition: none;
    }
  
    .more-banner {
      opacity: 1;
      transform: none;
    }
  
    .more-banner__image {
      opacity: 1;
      transform: none;
    }
  }

  /* Sorting analysis */

.sorting-analysis {
  position: absolute;
  z-index: 5;

  top: 249px;
  right: 2%;

  width: 31%;
  max-width: 410px;

  padding: 22px 24px;

  transform: translateY(-44%);

  color: #25282e;

  background:
    rgba(
      255,
      255,
      255,
      0.74
    );

  border:
    1px solid
    rgba(
      255,
      255,
      255,
      0.72
    );

  border-radius: 20px;

  box-shadow:
    0 18px 50px
    rgba(
      30,
      50,
      80,
      0.07
    );

  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);

  opacity: 0;

  transition:
    opacity 0.8s ease 0.3s,
    transform 0.8s
      cubic-bezier(
        0.22,
        1,
        0.36,
        1
      )
      0.3s;
}

.more-banner--visible
.sorting-analysis {
  opacity: 1;

  transform: translateY(-50%);
}

.sorting-analysis__label {
  display: inline-flex;
  align-items: center;

  margin-bottom: 11px;
  padding: 5px 8px;

  color: #2277e6;

  background:
    rgba(
      34,
      119,
      230,
      0.08
    );

  border:
    1px solid
    rgba(
      34,
      119,
      230,
      0.1
    );

  border-radius: 999px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;
  line-height: 1;

  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.sorting-analysis__title {
  margin:
    0
    0
    13px;

  color: #20242c;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 20px;
  line-height: 1.15;
  letter-spacing: -0.025em;
}

.sorting-analysis__content {
  display: flex;
  flex-direction: column;
  gap: 11px;
}

.sorting-analysis__content p {
  margin: 0;

  color: #626a77;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 11px;
  line-height: 1.48;
}

.sorting-analysis__result {
  padding:
    11px
    12px !important;

  color:
    #395a88 !important;

  background:
    linear-gradient(
      135deg,
      rgba(
        34,
        119,
        230,
        0.07
      ),
      rgba(
        83,
        104,
        232,
        0.045
      )
    );

  border:
    1px solid
    rgba(
      34,
      119,
      230,
      0.08
    );

  border-radius: 11px;
}

/* Dark */

html[data-theme="dark"]
.sorting-analysis {
  color: #ffffff;

  background:
    rgba(
      28,
      28,
      30,
      0.78
    );

  border-color:
    rgba(
      255,
      255,
      255,
      0.07
    );

  box-shadow:
    0 20px 55px
    rgba(
      0,
      0,
      0,
      0.22
    );
}

html[data-theme="dark"]
.sorting-analysis__label {
  color: #72a8ff;

  background:
    rgba(
      63,
      137,
      241,
      0.12
    );

  border-color:
    rgba(
      74,
      145,
      246,
      0.16
    );
}

html[data-theme="dark"]
.sorting-analysis__title {
  color: #f2f3f5;
}

html[data-theme="dark"]
.sorting-analysis__content p {
  color: #a7adb7;
}

html[data-theme="dark"]
.sorting-analysis__result {
  color:
    #9fc3ff !important;

  background:
    linear-gradient(
      135deg,
      rgba(
        40,
        135,
        247,
        0.12
      ),
      rgba(
        83,
        104,
        232,
        0.07
      )
    );

  border-color:
    rgba(
      74,
      145,
      246,
      0.14
    );
}

/* Laptop */

@media (max-width: 1366px) {
  .sorting-analysis {
    right: 0;

    width: 32%;
    max-width: 350px;

    padding: 17px 18px;

    border-radius: 16px;
  }

  .sorting-analysis__label {
    margin-bottom: 8px;

    font-size: 8px;
  }

  .sorting-analysis__title {
    margin-bottom: 10px;

    font-size: 17px;
  }

  .sorting-analysis__content {
    gap: 8px;
  }

  .sorting-analysis__content p {
    font-size: 9.5px;
    line-height: 1.42;
  }

  .sorting-analysis__result {
    padding:
      8px
      9px !important;
  }
}

/* 13" */

@media (
  min-width: 769px
) and (
  max-height: 820px
) {
  .sorting-analysis {
    width: 31%;
    max-width: 325px;

    padding: 14px 16px;
  }

  .sorting-analysis__title {
    margin-bottom: 8px;

    font-size: 15px;
  }

  .sorting-analysis__content {
    gap: 6px;
  }

  .sorting-analysis__content p {
    font-size: 8.5px;
    line-height: 1.38;
  }

  .sorting-analysis__result {
    padding:
      7px
      8px !important;
  }
}

/* Tablet / mobile */

@media (max-width: 900px) {
  .more-banner__visual {
    flex-direction: column;
  }

  .sorting-analysis {
    position: relative;

    top: auto;
    right: auto;

    width: 100%;
    max-width: 680px;

    margin-top: 16px;
    padding: 18px 20px;

    transform: translateY(10px);
  }

  .more-banner--visible
  .sorting-analysis {
    transform: translateY(0);
  }

  .sorting-analysis__title {
    font-size: 18px;
  }

  .sorting-analysis__content p {
    font-size: 11px;
  }
}

  </style>